import { createFileRoute, Link } from "@tanstack/react-router";
import { Heart, ShieldCheck, Sparkles as SparkIcon, Leaf } from "lucide-react";

import { BUSINESS } from "@/lib/site";
import { IMAGES, img } from "@/lib/images";
import { JsonLd } from "@/components/site/JsonLd";
import { localBusinessSchema, breadcrumbSchema } from "@/lib/schema";
import { FadeUp } from "@/components/site/FadeUp";
import { SectionHeading } from "@/components/site/SectionHeading";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { InnerHero } from "@/components/site/PageBits";
import { CtaBand } from "@/components/site/CTA";
import { LinkButton } from "@/components/site/Buttons";
import { useGoToQuote } from "@/lib/quoteNav";

const VALUES = [
  {
    icon: Heart,
    title: "Care for every space",
    body: "We treat your home or business like our own — with respect, attention to detail, and genuine pride in the results.",
  },
  {
    icon: ShieldCheck,
    title: "Reliable and consistent",
    body: "We show up when we say we will and deliver consistent results, visit after visit. No surprises, just a clean space.",
  },
  {
    icon: SparkIcon,
    title: "Attention to detail",
    body: "It's the corners, the baseboards, the spots others skip that make a space truly feel clean. That's our focus.",
  },
  {
    icon: Leaf,
    title: "A fresh, honest approach",
    body: "Clear communication, fair quotes, and no upsell games. We tell you what we'll do, then we do it.",
  },
];

export const Route = createFileRoute("/about")({
  head: () => ({
    meta: [
      { title: "About | Pretty in Pink Cleaning Service" },
      {
        name: "description",
        content:
          "Pretty in Pink Cleaning Service is a locally owned residential and commercial cleaning company in Wetumpka, AL. Learn about our values and approach.",
      },
      { property: "og:title", content: "About | Pretty in Pink Cleaning Service" },
      {
        property: "og:description",
        content: "A locally owned residential and commercial cleaning company in Wetumpka, AL.",
      },
      { property: "og:type", content: "website" },
      { property: "og:image", content: IMAGES.coverBanner },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: IMAGES.coverBanner },
    ],
  }),
  component: AboutPage,
});

function AboutPage() {
  const goQuote = useGoToQuote();
  return (
    <>
      <JsonLd data={localBusinessSchema()} />
      <JsonLd
        data={breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "About", path: "/about" },
        ])}
      />
      <InnerHero
        eyebrow="About Us"
        title="A pretty clean, every time"
        subtitle={`${BUSINESS.name} is a locally owned cleaning company in ${BUSINESS.city}, ${BUSINESS.state}, serving homes and businesses across the River Region.`}
      >
        <div className="mb-4">
          <Breadcrumbs
            items={[
              { name: "Home", path: "/" },
              { name: "About", path: "/about" },
            ]}
          />
        </div>
      </InnerHero>

      <section className="px-4 py-12 md:px-6 md:py-16">
        <FadeUp className="mx-auto max-w-3xl text-lg text-muted-foreground text-pretty">
          <p>
            We started {BUSINESS.name} with a simple goal: bring dependable, genuinely thorough
            cleaning to our neighbors in {BUSINESS.city} and the surrounding communities.{" "}
            {BUSINESS.offers.toLowerCase()} is what we do — for busy families, for small businesses,
            for anyone who'd rather spend their time on something other than scrubbing.
          </p>
          <p className="mt-4">
            {BUSINESS.tagline} That's more than a tagline. It's the standard we hold every visit to.
            We sweat the details — the baseboards, the light switches, the corners dust settles into
            — so your space doesn't just look clean, it feels clean.
          </p>
          <p className="mt-4">
            {BUSINESS.secondary} From a recurring weekly clean to a one-time deep clean or a
            move-out reset, we tailor what we do to how you use your space.
          </p>
        </FadeUp>
        <div className="mx-auto mt-10 grid max-w-5xl gap-5 sm:grid-cols-2">
          <FadeUp>
            <div className="overflow-hidden rounded-3xl border border-border shadow-[0_30px_60px_-40px_rgba(26,26,26,0.4)]">
              <img
                src={img(IMAGES.fireplace1, 1200)}
                srcSet={`${img(IMAGES.fireplace1, 640)} 640w, ${img(IMAGES.fireplace1, 1200)} 1200w`}
                sizes="(min-width: 768px) 50vw, 100vw"
                alt="Brick fireplace and plank floors in a living room cleaned by Pretty in Pink Cleaning Service in Wetumpka, AL"
                loading="lazy"
                decoding="async"
                width={1200}
                height={1500}
                className="aspect-[4/5] w-full object-cover"
              />
            </div>
          </FadeUp>
          <FadeUp delay={80}>
            <div className="overflow-hidden rounded-3xl border border-border shadow-[0_30px_60px_-40px_rgba(26,26,26,0.4)]">
              <img
                src={img(IMAGES.stairs1, 1200)}
                srcSet={`${img(IMAGES.stairs1, 640)} 640w, ${img(IMAGES.stairs1, 1200)} 1200w`}
                sizes="(min-width: 768px) 50vw, 100vw"
                alt="Vacuumed carpeted staircase cleaned by Pretty in Pink Cleaning Service in Wetumpka, AL"
                loading="lazy"
                decoding="async"
                width={1200}
                height={1500}
                className="aspect-[4/5] w-full object-cover"
              />
            </div>
          </FadeUp>
        </div>
      </section>

      <section className="bg-secondary/40 px-4 py-12 md:px-6 md:py-16">
        <FadeUp>
          <SectionHeading
            eyebrow="Our Values"
            title="What we believe in"
            intro="The principles behind every visit — and the reason clients trust us with their spaces."
          />
        </FadeUp>
        <div className="mx-auto mt-10 grid max-w-6xl gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {VALUES.map((v, i) => (
            <FadeUp key={v.title} delay={i * 70}>
              <div className="lift-card h-full rounded-2xl border border-border bg-card p-6">
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <v.icon className="h-6 w-6" />
                </span>
                <h3 className="mt-4 text-lg font-bold text-ink">{v.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground text-pretty">{v.body}</p>
              </div>
            </FadeUp>
          ))}
        </div>
      </section>

      <section className="px-4 py-12 md:px-6 md:py-16">
        <FadeUp className="mx-auto max-w-3xl text-center">
          <h2 className="text-2xl font-bold text-ink md:text-3xl">
            Locally owned, proudly serving Wetumpka
          </h2>
          <p className="mt-3 text-muted-foreground text-pretty">
            We live and work in the River Region. When you hire {BUSINESS.name}, you're hiring a
            neighbor — and we treat your space the way we'd want ours treated.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <button
              type="button"
              onClick={goQuote}
              className="btn-sparkle inline-flex items-center justify-center bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground"
            >
              <span>Get a Quote</span>
            </button>
            <LinkButton href={BUSINESS.phoneHref} variant="outline">
              Call {BUSINESS.phoneDisplay}
            </LinkButton>
          </div>
        </FadeUp>
      </section>

      <CtaBand />
    </>
  );
}
